import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	type ExtensionManifest,
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
import { DBExperienceManifest } from '@atlassian/native-embeds-db-experience';
import { MauiExperienceManifest } from '@atlassian/native-embeds-maui-experience';
import { PageExperienceManifest } from '@atlassian/native-embeds-page-experience';
import { WhiteboardExperienceManifest } from '@atlassian/native-embeds-whiteboard-experience';

getNativeEmbedFacade().registerExperienceManifest(WhiteboardExperienceManifest);
getNativeEmbedFacade().registerExperienceManifest(DBExperienceManifest);
getNativeEmbedFacade().registerExperienceManifest(MauiExperienceManifest);
getNativeEmbedFacade().registerExperienceManifest(PageExperienceManifest);

const manifest: ExtensionManifest = {
	title: 'Native Embed',
	type: NATIVE_EMBED_EXTENSION_TYPE,
	key: NATIVE_EMBED_EXTENSION_KEY,
	description: 'Embed native Atlassian content',
	icons: {
		'16': () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_editor-tray" */ '@atlaskit/icon/core/tools'
			).then((mod) => mod.default),
		'24': () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_editor-tray" */ '@atlaskit/icon/core/tools'
			).then((mod) => mod.default),
		'48': () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_editor-tray" */ '@atlaskit/icon/core/tools'
			).then((mod) => mod.default),
	},
	modules: {
		quickInsert: [
			{
				key: `${NATIVE_EMBED_EXTENSION_KEY}:pages`,
				title: 'Native Embed',
				description: 'Embed native Atlassian content',
				icon: () =>
					import(
						/* webpackChunkName: "@atlaskit-internal_editor-tray" */ '@atlaskit/icon/core/tools'
					).then((mod) => mod.default),
				keywords: ['native', 'embed', 'native-embed'],
				action: {
					type: 'node',
					key: 'default',
					parameters: {},
				},
			},
		],
		nodes: {
			default: {
				type: 'extension',
				render: () =>
					import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'),
				// @ts-expect-error This option is experimental and not reflected in the manifest type definition
				__hideFrame: true,
			},
			...Object.fromEntries(
				getNativeEmbedFacade()
					.getAllExperienceIds()
					.map((experienceId) => [
						experienceId,
						{
							type: 'extension',
							render: () =>
								import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'),
							__hideFrame: true,
						},
					]),
			),
		},
		autoConvert: {
			url: [
				(text: string): ADFEntity | undefined => {
					// Check if URL matches any registered experience patterns
					const experienceId = getNativeEmbedFacade().getExperienceIdFromUrl(text);
					if (experienceId) {
						return {
							type: 'extension',
							attrs: {
								extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:${experienceId}`,
								extensionType: NATIVE_EMBED_EXTENSION_TYPE,
								parameters: {
									url: text,
								},
							},
						};
					}

					// Return undefined if URL doesn't match - handled by smart links instead.
					return undefined;
				},
			],
		},
	},
};

export default manifest;
